import React, { useMemo, useState } from "react";
import { isEmpty, snakeCase } from "lodash";
import styled from "styled-components";
import { Spacing, dominant } from "@glossy";
import { Description, Toggle } from "@ui-kit/form";
import Icon from "@ui-kit/icon";
import Dropdown from "@radarly/components/panels/card-edition/sidebar/dropdown";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { tr } from "@radarly/i18n";
import {
  editAINegativeAnalysisOnCard,
  editAITypeOnCard,
} from "@radarly/models/cards/editor";
import { AICard } from "@radarly/models/cards/model";
import { DatavizTypeAI } from "@radarly/models/dataviz";
import useEditIpDescriptionModal from "./useEditIpAIContextModal";

export interface SharedPanelsCardEditionSidebarOptionsEmvProps {
  setCard?: (card: AICard) => void;
  card?: AICard;
  aiContext: string;
  setAIContext: (description: string) => void;
}

const IPDescriptionRoot = styled(Description)`
  display: flex;
  gap: ${Spacing.Micro};
`;

const IPDescriptionIcon = styled(Icon)`
  flex-shrink: 0;
  margin: 2px;
`;

const IPDescription = styled.div`
  a {
    color: ${dominant};
    text-decoration: underline;
  }
`;

export default function SharedPanelsCardEditionSidebarOptionsAI({
  setCard,
  card,
  aiContext,
  setAIContext,
}: SharedPanelsCardEditionSidebarOptionsEmvProps) {
  const [localAIContext, setLocalAIContext] = useState(aiContext);

  const locale = useCurrentUserLocale();
  const typeAnalysis = card?.fragments[0].analysis?.type;

  const labelsTranslation = useMemo(
    () => ({
      "general-analysis": tr(
        locale,
        "CUSTOM_PAGES::EDITOR::CARD_LIBRARY::GENERAL_ANALYSIS"
      ),
      "signal-analysis": tr(
        locale,
        "CUSTOM_PAGES::EDITOR::CARD_LIBRARY::SIGNAL_ANALYSIS"
      ),
      "risk-analysis": tr(
        locale,
        "CUSTOM_PAGES::EDITOR::CARD_LIBRARY::RISK_ANALYSIS"
      ),
      "audience-analysis": tr(
        locale,
        "CUSTOM_PAGES::EDITOR::CARD_LIBRARY::AUDIENCE_ANALYSIS"
      ),
    }),
    [locale]
  );

  const options = [
    // {
    //   value: "publication-overview-analysis",
    //   label: labelsTranslation["publication-overview-analysis"],
    // },
    {
      value: "general-analysis",
      label: labelsTranslation["general-analysis"],
    },
    {
      value: "signal-analysis",
      label: labelsTranslation["signal-analysis"],
    },
    {
      value: "risk-analysis",
      label: labelsTranslation["risk-analysis"],
    },
    {
      value: "audience-analysis",
      label: labelsTranslation["audience-analysis"],
    },
  ];

  const option = {
    value: typeAnalysis,
    label: labelsTranslation[typeAnalysis],
  };

  const isEmptyDescription = isEmpty(localAIContext);
  const { EditIpAIContext, onOpenEditAIContextModal } =
    useEditIpDescriptionModal(locale, () => setAIContext(localAIContext));

  return (
    <>
      {EditIpAIContext(localAIContext, setLocalAIContext)}
      <Dropdown
        label={tr(locale, "CUSTOM_PAGES::EDITOR::DATAVIZ::AI::ANALYSIS_TYPE")}
        value={option}
        options={options}
        onChange={({ value }) =>
          setCard(
            editAITypeOnCard(
              card,
              value as DatavizTypeAI,
              labelsTranslation[value]
            )
          )
        }
      />
      <Description>
        {tr(
          locale,
          "CUSTOM_PAGES::EDITOR::DATAVIZ::AI::DESCRIPTION_" +
            snakeCase(typeAnalysis)
        )}
      </Description>
      {typeAnalysis === "general-analysis" && (
        <Toggle
          label={tr(
            locale,
            "CUSTOM_PAGES::EDITOR::DATAVIZ::AI::NEGATIVE_ANALYSIS"
          )}
          checked={card?.fragments[0].analysis?.includeNegativeAnalysis}
          onChange={(_, checked) =>
            setCard(editAINegativeAnalysisOnCard(card, checked))
          }
        />
      )}
      <IPDescriptionRoot>
        <IPDescriptionIcon glyph={isEmptyDescription ? "bang" : "i"} disk />
        <IPDescription
          onClick={(e) => {
            if ((e as any)?.target?.localName === "a") {
              onOpenEditAIContextModal();
            }
          }}
          dangerouslySetInnerHTML={{
            __html: isEmptyDescription
              ? tr(
                  locale,
                  "CUSTOM_PAGES::EDITOR::DATAVIZ::IP_DESCRIPTION::EMPTY"
                )
              : tr(
                  locale,
                  "CUSTOM_PAGES::EDITOR::DATAVIZ::IP_DESCRIPTION::FILLED"
                ),
          }}
        />
      </IPDescriptionRoot>
    </>
  );
}
